import { apiGet, apiPut, apiPost } from './client';
import type { Lead, LeadDetail, LeadStatus, ActivityType } from '@/types';

export interface LeadsResponse {
  leads: Lead[];
  total: number;
  page: number;
  page_size: number;
}

export interface LeadsParams {
  page?: number;
  page_size?: number;
  search?: string;
  status?: string;
}

export interface UpdateLeadData {
  status?: LeadStatus;
  phone?: string;
  budget?: string;
  timeline?: string;
  notes?: string;
}

export interface LogActivityData {
  activity_type: ActivityType;
  description: string;
}

export async function getLeads(params?: LeadsParams): Promise<LeadsResponse> {
  return apiGet<LeadsResponse>('/leads', params as Record<string, unknown>);
}

export async function getLead(id: string): Promise<Lead> {
  return apiGet<Lead>(`/leads/${id}`);
}

export async function getLeadDetail(id: string): Promise<LeadDetail> {
  return apiGet<LeadDetail>(`/leads/${id}/detail`);
}

export async function updateLead(id: string, data: UpdateLeadData): Promise<Lead> {
  return apiPut<Lead>(`/site-admin/leads/${id}`, data);
}

export async function updateLeadStatus(id: string, status: LeadStatus): Promise<Lead> {
  return apiPut<Lead>(`/site-admin/leads/${id}`, { status });
}

export async function logLeadActivity(id: string, data: LogActivityData): Promise<LeadDetail> {
  return apiPost<LeadDetail>(`/leads/${id}/activities`, data);
}

// ── Buyer briefing (V3.1 saved searches + alerts) ────────────────────────────

export interface BuyerBriefing {
  visitor_id: string;
  /** Distinct listings viewed in the last 30 days. */
  viewed_30d: number;
  favorites: number;
  saved_searches: Array<{ id: string; name: string; criteria: Record<string, string | number>; frequency: 'instant' | 'daily' | 'off' }>;
  price_range: { min: number | null; max: number | null };
  zips: string[];
  cities: string[];
  last_seen_at: string | null;
  scores: { buyer: number; seller: number };
  /** "14 listings viewed, 3 favorites, <$450K, zips 33484/33445" */
  summary: string;
}

/**
 * What the buyer did on the site, resolved from the lead (leads.visitor_id, else the contact
 * with the lead's email). Null when the lead has no site activity yet — the page shows no card.
 */
export async function fetchLeadBriefing(leadId: string): Promise<BuyerBriefing | null> {
  try {
    return await apiGet<BuyerBriefing>(`/ai-marketer/leads/${encodeURIComponent(leadId)}/briefing`);
  } catch (err) {
    const status = (err as { response?: { status?: number } })?.response?.status;
    if (status === 404) return null;
    throw err;
  }
}
